import React from 'react';

import { FormLayout } from '@/components/layout';
import { Banner } from '@/components/ui/banners';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { NumericSpinner } from '@/components/ui/inputs/NumericSpinner';
import { Switch } from '@/components/ui/inputs/Switch';
import { DatePicker } from '@/components/ui/filters';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import { useTranslation } from '@/i18n';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import type { UpdateShiftDescriptionFormSubset } from '@/schemas/shift/updateShiftDescriptionForm';
import { DtResolutionStatusBanners } from './DtResolutionStatusBanners';


interface UpdateShiftDescriptionFormProps {
  form: UpdateShiftDescriptionFormSubset;
  shiftCode: string;
  workingDayTypeOptions: SelectorOption[];
  workingDayOptions: SelectorOption[];
  isDistributedCyclesType: boolean;
  isExceptionalType: boolean;
  isStaticType: boolean;
  weeksCountEditable: boolean;
  ordinaryHours: number | null;
  /** true when the shift is assigned (or the user lacks edit permission) — locks the whole form. */
  isReadOnly: boolean;
  /**
   * Screen-level edit permission. Gates the status switch on its own: toggling
   * Activo/Inactivo is the ONLY action allowed on an assigned (read-only) shift,
   * so the control must stay visible and merely disabled when permission is
   * missing — never hidden, which would leave the screen with no available action.
   */
  canEdit: boolean;
  isEnabled: boolean;
  isTogglingStatus: boolean;
  onToggleStatus: () => void;
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const UpdateShiftDescriptionForm: React.FC<UpdateShiftDescriptionFormProps> = ({
  form,
  shiftCode,
  workingDayTypeOptions,
  workingDayOptions,
  isDistributedCyclesType,
  isExceptionalType,
  isStaticType,
  weeksCountEditable,
  ordinaryHours,
  isReadOnly,
  canEdit,
  isEnabled,
  isTogglingStatus,
  onToggleStatus,
}) => {
  const { t: translate } = useTranslation('schedule');
  const { register, watch, setValue, errors } = form;

  const showDtAuthorizationFields =
    isExceptionalType || (isDistributedCyclesType && watch('isExceptionalWorkingDay'));

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="update-shift-description-form">
      <FormLayout.Section title={translate('shift.create.sections.description')}>
        {isReadOnly && (
          <div className="col-span-full">
            <Banner
              variant="warning"
              text={translate('shift.edit.messages.readOnlyWarning')}
              data-testid="shift-edit-readonly-banner"
            />
          </div>
        )}

        <Selector
          label={translate('shift.create.fields.workingDayType')}
          required
          disabled={isReadOnly}
          options={workingDayTypeOptions}
          value={watch('workingDayTypeId') != null ? String(watch('workingDayTypeId')) : undefined}
          onChange={(value) => {
            setValue('workingDayTypeId', normalizeNumericSelectValue(value) ?? null, { shouldDirty: true, shouldValidate: true });
            setValue('workingDayId', null, { shouldDirty: true });
          }}
          color={errors.workingDayTypeId ? 'error' : 'default'}
          supportingText={
            errors.workingDayTypeId?.message
              ? translate(errors.workingDayTypeId.message)
              : undefined
          }
          data-testid="shift-working-day-type-selector"
        />

        <Selector
          label={translate('shift.create.fields.workingDay')}
          required
          disabled={isReadOnly}
          options={workingDayOptions}
          value={watch('workingDayId') != null ? String(watch('workingDayId')) : undefined}
          onChange={(value) => {
            setValue('workingDayId', normalizeNumericSelectValue(value) ?? null, { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.workingDayId ? 'error' : 'default'}
          supportingText={
            errors.workingDayId?.message ? translate(errors.workingDayId.message) : undefined
          }
          data-testid="shift-working-day-selector"
        />

        <LabelInput
          label={translate('shift.create.fields.ordinaryHours')}
          value={ordinaryHours !== null ? String(ordinaryHours) : ''}
          readOnly
          data-testid="shift-ordinary-hours-input"
        />

        <NumericSpinner
          label={translate('shift.create.fields.weeksCount')}
          value={watch('weeksCount') ?? 1}
          onChange={(value) => setValue('weeksCount', value, { shouldDirty: true, shouldValidate: true })}
          min={1}
          padLength={0}
          disabled={isReadOnly || !weeksCountEditable}
          data-testid="shift-weeks-count-spinner"
        />

        <LabelInput
          label={translate('shift.create.fields.shiftCode')}
          value={shiftCode}
          disabled
          tooltipTitle={translate('shift.edit.fields.shiftCodeTooltipTitle')}
          tooltipText={translate('shift.edit.fields.shiftCodeTooltipBody')}
          data-testid="shift-code-input"
        />

        <LabelInput
          label={translate('shift.create.fields.shiftName')}
          required
          disabled={isReadOnly}
          registration={register('shiftName')}
          color={errors.shiftName ? 'error' : 'default'}
          supportingText={
            errors.shiftName?.message ? translate(errors.shiftName.message) : undefined
          }
          data-testid="shift-name-input"
        />

        {isStaticType && (
          <DatePicker
            label={translate('shift.create.fields.staticStartDate')}
            required
            disabled={isReadOnly}
            value={watch('staticStartDate')}
            onChange={(date) => setValue('staticStartDate', date, { shouldDirty: true, shouldValidate: true })}
            color={errors.staticStartDate ? 'error' : 'default'}
            supportingText={
              errors.staticStartDate?.message
                ? translate(errors.staticStartDate.message)
                : undefined
            }
            data-testid="shift-static-start-date-picker"
          />
        )}

        {isDistributedCyclesType && (
          <Checkbox
            label={translate('shift.create.fields.isExceptionalWorkingDay')}
            checked={watch('isExceptionalWorkingDay')}
            disabled={isReadOnly}
            onChange={(checked) => setValue('isExceptionalWorkingDay', checked, { shouldDirty: true, shouldValidate: true })}
            data-testid="shift-is-exceptional-checkbox"
          />
        )}

        {showDtAuthorizationFields && (
          <>
            <LabelInput
              label={translate('shift.create.fields.dtAuthorizationNumber')}
              required
              disabled={isReadOnly}
              registration={register('dtAuthorizationNumber')}
              color={errors.dtAuthorizationNumber ? 'error' : 'default'}
              supportingText={
                errors.dtAuthorizationNumber?.message
                  ? translate(errors.dtAuthorizationNumber.message)
                  : undefined
              }
              data-testid="shift-dt-authorization-number-input"
            />

            <DatePicker
              label={translate('shift.create.fields.dtAuthorizationDate')}
              required
              disabled={isReadOnly}
              value={watch('dtAuthorizationDate')}
              onChange={(date) => setValue('dtAuthorizationDate', date, { shouldDirty: true, shouldValidate: true })}
              color={errors.dtAuthorizationDate ? 'error' : 'default'}
              supportingText={
                errors.dtAuthorizationDate?.message
                  ? translate(errors.dtAuthorizationDate.message)
                  : undefined
              }
              data-testid="shift-dt-authorization-date-picker"
            />

            <DtResolutionStatusBanners dtAuthorizationDate={watch('dtAuthorizationDate')} />
          </>
        )}

        {/* El interruptor de estado va SIEMPRE al final, después de los campos
            condicionales (fecha de inicio / resolución DT), según el diseño. */}
        <Switch
          checked={isEnabled}
          onChange={onToggleStatus}
          disabled={!canEdit || isTogglingStatus}
          label={translate(isEnabled ? 'shift.filters.active' : 'shift.filters.inactive')}
          tooltipTitle={translate('shift.edit.fields.statusTooltipTitle')}
          tooltipText={translate('shift.edit.fields.statusTooltipBody')}
          data-testid="shift-status-switch"
        />
      </FormLayout.Section>
    </FormLayout>
  );
};

export default UpdateShiftDescriptionForm;
